import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";

/**
 * 🎙️ Live Now Strip — surfaces the top 3 active rooms on Discover.
 *
 * Mobile-first horizontal rail. Auto-polls every 10s so listener counts
 * + gift totals stay live without a page reload. Audio-safe: uses
 * <Link> only — never affects the AudioPlayer subtree on Discover.
 */

const LiveCard = ({ room }) => (
  <Link
    to={`/beams/${room.room_id}`}
    className="snap-start flex-shrink-0 w-[280px] sm:w-[320px] p-4 rounded-2xl bg-gradient-to-br from-rose-600/30 via-pink-600/25 to-orange-500/25 border border-rose-500/50 hover:border-rose-400 transition-all hover:scale-[1.02] shadow-lg shadow-rose-500/10 relative overflow-hidden"
    data-testid={`live-now-card-${room.room_id}`}
  >
    {/* Phase 21.1a — Live cover backdrop (when set by host). */}
    {room.cover_image && (
      <img
        src={optimizeImage(room.cover_image, 'thumb')}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none"
        data-testid={`live-now-cover-${room.room_id}`}
        onError={(e) => { e.target.style.display = "none"; }}
      />
    )}
    <div className="relative flex items-start gap-3">
      <div className="relative">
        <img
          src={getProfilePhotoUrl({
            profile_photo: room.host_profile_photo,
            picture: room.host_profile_photo,
          })}
          alt={room.host_name}
          className="w-14 h-14 rounded-full object-cover border-2 border-rose-400/70 ring-2 ring-rose-500/30"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop"; }}
        />
        <span
          className="absolute -top-1 -right-1 text-[9px] bg-rose-500 text-white px-1.5 rounded-full font-black animate-pulse"
          data-testid="live-now-pulse"
        >
          LIVE
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold truncate text-sm">{room.title}</p>
        <p className="text-[11px] text-rose-100/80 truncate">
          🎙️ {room.host_name || "Anonymous"}
        </p>
        {room.topic && (
          <p className="text-[10px] text-orange-100/70 truncate mt-0.5">
            “{room.topic}”
          </p>
        )}
      </div>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] relative">
      <div className="px-2 py-1 rounded-lg bg-white/10 border border-white/15">
        <p className="text-rose-100/70 text-[9px] uppercase tracking-wider font-semibold">Listeners</p>
        <p className="text-white font-bold" data-testid={`live-now-listeners-${room.room_id}`}>
          🟢 {room.online_count || 0}
        </p>
      </div>
      <div className="px-2 py-1 rounded-lg bg-white/10 border border-white/15">
        <p className="text-amber-100/70 text-[9px] uppercase tracking-wider font-semibold">Gifted</p>
        <p className="text-amber-200 font-bold">
          🪙 {(room.gift_total_coins || 0).toLocaleString()}
        </p>
      </div>
    </div>
    <div className="relative mt-3 text-center text-xs font-bold py-1.5 rounded-lg bg-gradient-to-r from-rose-500 to-pink-500 text-white">
      Join Beam →
    </div>
  </Link>
);

const LiveNowStrip = () => {
  const [rooms, setRooms] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const r = await api.get("/live/now?limit=3");
        if (!cancelled) {
          // Dedup by room_id (defensive — backend should already guarantee this)
          const seen = new Set();
          setRooms((r.data?.rooms || []).filter((x) => {
            if (seen.has(x.room_id)) return false;
            seen.add(x.room_id);
            return true;
          }));
        }
      } catch (_e) {
        if (!cancelled) setRooms([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    fetch();
    const t = setInterval(fetch, 10000); // real-time refresh every 10s
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!loaded || rooms.length === 0) return null;

  return (
    <section className="mb-6 sm:mb-8" data-testid="live-now-strip">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className="text-2xl animate-pulse">🎙️</span>
          <div>
            <h2 className="text-lg sm:text-xl font-black text-white">
              Live Now
              <span
                className="ml-2 text-[9px] align-middle bg-rose-500 text-white px-1.5 py-0.5 rounded-full font-black animate-pulse"
              >
                LIVE
              </span>
            </h2>
            <p className="text-[10px] text-rose-200/70">
              Hop in. Real conversations happening right now.
            </p>
          </div>
        </div>
        <Link
          to="/beams"
          className="text-xs text-rose-300 hover:text-rose-200 font-semibold"
          data-testid="live-now-view-all"
        >
          See all →
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1">
        {rooms.map((r) => (
          <LiveCard key={r.room_id} room={r} />
        ))}
      </div>
    </section>
  );
};

export default LiveNowStrip;
